import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StripeProvider } from './contexts/StripeContext';
import Layout from './components/Layout';
import LanguageManager from './components/LanguageManager';

// Lazy load all page components for code-splitting
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const QRScanner = lazy(() => import('./pages/QRScanner'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const ServiceLogging = lazy(() => import('./pages/ServiceLogging'));
const PublicClientProfile = lazy(() => import('./pages/PublicClientProfile'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const QRCardGenerator = lazy(() => import('./pages/QRCardGenerator'));
const FieldWorkers = lazy(() => import('./pages/FieldWorkers'));
const OrganizationManagement = lazy(() => import('./pages/OrganizationManagement'));
const OrganizationAdmins = lazy(() => import('./pages/OrganizationAdmins'));
const ClientRegistration = lazy(() => import('./pages/ClientRegistration'));
const BillingDashboard = lazy(() => import('./pages/BillingDashboard'));
const SubscriptionSetup = lazy(() => import('./components/SubscriptionSetup'));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="expanse-card p-8 text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// QR Code Handler Component - handles direct QR code links (public access)
const QRCodeHandler: React.FC = () => {
  const { user } = useAuth();
  const [qrLoading, setQrLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Extract QR code from URL and handle accordingly
  const pathParts = window.location.pathname.split('/');
  const qrCode = pathParts[2]; // /qr/CODE_HERE

  // Handle QR code lookup (works for both authenticated and unauthenticated users)
  React.useEffect(() => {
    if (!qrCode) return;

    const handleQRCodeLookup = async () => {
      setQrLoading(true);
      setError(null);

      try {
        // Use direct QR endpoint via API - no authentication required
        const response = await fetch(`/api/qr/${qrCode}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          if (data.data.isRegistered && data.data.clientId) {
            // Navigate to authenticated client profile if user is logged in, otherwise public profile
            if (user) {
              window.location.href = `/client/${data.data.clientId}`;
            } else {
              window.location.href = data.data.redirect;
            }
          } else {
            // QR code is unregistered - check if user is logged in
            if (user) {
              // User is logged in, redirect to registration
              window.location.href = `/register-client?qrCode=${qrCode}`;
            } else {
              // User is not logged in, redirect to login with redirect back to this QR code
              const currentPath = window.location.pathname;
              localStorage.setItem('redirectAfterLogin', currentPath);
              window.location.href = '/login';
            }
          }
        } else {
          setError(data.error?.message || 'QR code not found');
        }
      } catch (err) {
        console.error('QR lookup error:', err);
        setError('Failed to lookup QR code');
      } finally {
        setQrLoading(false);
      }
    };

    handleQRCodeLookup();
  }, [qrCode, user]);


  if (!qrCode) {
    return <Navigate to="/dashboard" replace />;
  }

  if (qrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="expanse-card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Looking up QR code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="expanse-card p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">QR Code Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <a href="/dashboard" className="btn-primary">
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return null;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'ngo_admin' && user.role !== 'system_admin') {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const SystemAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'system_admin') {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <StripeProvider>
        <LanguageManager />
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/public/client/:id" element={<PublicClientProfile />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-management"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ClientManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ClientProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qr-scanner"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <QRScanner />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceLogging />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cr-card-generator"
                element={
                  <SystemAdminRoute>
                    <Layout>
                      <QRCardGenerator />
                    </Layout>
                  </SystemAdminRoute>
                }
              />
              <Route
                path="/field-workers"
                element={
                  <AdminRoute>
                    <Layout>
                      <FieldWorkers />
                    </Layout>
                  </AdminRoute>
                }
              />
              <Route
                path="/organization-admins"
                element={
                  <AdminRoute>
                    <Layout>
                      <OrganizationAdmins />
                    </Layout>
                  </AdminRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <SystemAdminRoute>
                    <Layout>
                      <OrganizationManagement />
                    </Layout>
                  </SystemAdminRoute>
                }
              />
              <Route
                path="/register-client"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ClientRegistration />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <AdminRoute>
                    <Layout>
                      <BillingDashboard />
                    </Layout>
                  </AdminRoute>
                }
              />
              <Route
                path="/billing/setup"
                element={
                  <AdminRoute>
                    <Layout>
                      <SubscriptionSetup onSuccess={() => window.location.href = '/billing'} />
                    </Layout>
                  </AdminRoute>
                }
              />
              {/* QR Code Direct Access Route */}
              <Route
                path="/qr/:qrCode"
                element={<QRCodeHandler />}
              />
            </Routes>
          </Suspense>
        </Router>
      </StripeProvider>
    </AuthProvider>
  );
}

export default App;